import { useEffect, useRef, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { motion, useScroll, useTransform, useReducedMotion, useInView } from 'framer-motion'
import {
  ArrowRight,
  FileText,
  Users,
  ShieldCheck,
  AlertTriangle,
  DollarSign,
  HelpCircle,
  LayoutGrid,
  User,
  LogOut,
} from 'lucide-react'
import { PublicLayout } from '@/components/PublicLayout'
import { LandingButton } from '@/components/LandingButton'
import { useAuth } from '@/lib/auth'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EASE_STANDARD: [number, number, number, number] = [0.25, 0.46, 0.45, 0.94]
const EASE_OVERSHOOT: [number, number, number, number] = [0.34, 1.56, 0.64, 1]

const ILLUSTRATIONS = {
  working: '/illustrations/working.svg',
  hiring: '/illustrations/hiring.svg',
  businessDeal: '/illustrations/business-deal.svg',
  agreement: '/illustrations/agreement.svg',
}

// ---------------------------------------------------------------------------
// AnimatedStat — counts up on scroll
// ---------------------------------------------------------------------------

function AnimatedStat({
  end,
  suffix,
  label,
  isFloat = false,
}: {
  end: number
  suffix: string
  label: string
  isFloat?: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, amount: 0.5 })
  const [display, setDisplay] = useState(isFloat ? '0.0' : '0')

  useEffect(() => {
    if (!isInView) return

    const duration = 800
    const start = performance.now()

    function tick(now: number) {
      const progress = Math.min((now - start) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3) // ease-out cubic
      const current = eased * end
      setDisplay(isFloat ? current.toFixed(1) : String(Math.round(current)))
      if (progress < 1) requestAnimationFrame(tick)
    }

    requestAnimationFrame(tick)
  }, [isInView, end, isFloat])

  return (
    <div ref={ref} className="text-center">
      <div
        className="mb-1 text-4xl font-extrabold text-primary"
        style={{ fontFamily: 'Fraunces, Georgia, serif', fontVariantNumeric: 'tabular-nums' }}
      >
        {display}
        {suffix}
      </div>
      <div className="text-[13px] text-muted-foreground">{label}</div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Confetti (one-shot on match card)
// ---------------------------------------------------------------------------

function ConfettiDot({
  delay,
  color,
  style,
}: {
  delay: number
  color: string
  style: React.CSSProperties
}) {
  return (
    <motion.span
      className="absolute h-[5px] w-[5px] rounded-full"
      style={{ backgroundColor: color, ...style }}
      initial={{ opacity: 0, y: 0, scale: 0 }}
      animate={{ opacity: [0, 1, 0], y: [0, -10, -24], scale: [0, 1.2, 0.4] }}
      transition={{ duration: 0.7, ease: 'easeOut', delay }}
    />
  )
}

// ---------------------------------------------------------------------------
// Hero Section
// ---------------------------------------------------------------------------

function HeroSection() {
  const prefersReducedMotion = useReducedMotion()

  const fadeUp = useCallback(
    (delay: number, yStart = 40) => ({
      initial: prefersReducedMotion ? false : ({ opacity: 0, y: yStart } as const),
      animate: { opacity: 1, y: 0 },
      transition: { duration: 0.65, ease: EASE_STANDARD, delay },
    }),
    [prefersReducedMotion]
  )

  const notifications = [
    { dot: 'bg-primary', title: 'Project Posted', subtitle: 'Bakery Inventory System', delay: 0.6 },
    {
      dot: 'bg-accent',
      title: '3 Proposals Received',
      subtitle: 'From vetted developers',
      delay: 1.3,
    },
    {
      dot: 'bg-[#6B9BD2]',
      title: 'Developer Matched!',
      subtitle: '98% skill match',
      delay: 2.0,
      isMatch: true,
    },
  ]

  return (
    <section className="relative flex min-h-[88vh] items-center overflow-hidden px-8 pb-16 pt-20">
      {/* Gradient mesh background */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 20% 40%, rgba(91,146,121,0.07) 0%, transparent 70%), radial-gradient(ellipse 60% 50% at 80% 30%, rgba(232,152,90,0.06) 0%, transparent 70%)',
        }}
      />

      <div className="relative z-10 mx-auto grid w-full max-w-[1120px] grid-cols-1 items-center gap-12 lg:grid-cols-[1fr_1.1fr]">
        {/* Left: Text */}
        <div>
          <motion.h1
            {...fadeUp(0)}
            className="mb-6 text-foreground"
            style={{
              fontFamily: 'Fraunces, Georgia, serif',
              fontSize: 'clamp(40px, 5vw, 56px)',
              fontWeight: 700,
              lineHeight: 1.08,
              letterSpacing: '-1.5px',
            }}
          >
            From Idea to Custom Software,
            <br />
            Built on{' '}
            <span className="bg-gradient-to-br from-primary to-[#6BAE8A] bg-clip-text text-transparent">
              Trust
            </span>
            .
          </motion.h1>

          <motion.p
            {...fadeUp(0.12, 30)}
            className="mb-9 max-w-[440px] text-lg leading-[1.65] text-muted-foreground"
          >
            Ready to scale with confidence? Masonyt is the trusted marketplace where
            businesses build quality custom software with elite, vetted developers.
          </motion.p>

          <motion.div {...fadeUp(0.24, 24)}>
            <LandingButton to="/post">
              Post Your Project
              <ArrowRight className="h-[18px] w-[18px] transition-transform group-hover:translate-x-[3px]" />
            </LandingButton>
          </motion.div>

          <motion.p
            initial={prefersReducedMotion ? false : { opacity: 0 }}
            animate={{ opacity: 0.8 }}
            transition={{ duration: 0.5, ease: 'easeOut', delay: 0.36 }}
            className="mt-3.5 text-[13px] text-muted-foreground"
          >
            Free to post &middot; Only pay when you approve
          </motion.p>
        </div>

        {/* Right: Illustration + Notification cards */}
        <motion.div
          className="relative flex items-center justify-center"
          initial={prefersReducedMotion ? false : { opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.75, ease: EASE_STANDARD, delay: 0.15 }}
        >
          <img
            src={ILLUSTRATIONS.working}
            alt="Person working at desk"
            className="w-full max-w-[520px] drop-shadow-[0_8px_32px_rgba(45,42,38,0.06)]"
            loading="eager"
          />

          {/* Notification cards */}
          <div className="absolute right-[-20px] top-1/2 z-20 flex -translate-y-1/2 flex-col gap-3 max-lg:relative max-lg:right-auto max-lg:top-auto max-lg:mt-6 max-lg:translate-y-0">
            {notifications.map((notif) => (
              <motion.div
                key={notif.title}
                className={`relative flex cursor-default items-center gap-3 whitespace-nowrap rounded-[14px] border border-border/60 bg-card px-[18px] py-3.5 shadow-[0_4px_20px_rgba(45,42,38,0.1)] transition-[transform,box-shadow] duration-200 hover:-translate-x-[3px] hover:shadow-[0_6px_24px_rgba(45,42,38,0.14)] ${notif.isMatch ? 'overflow-visible' : ''}`}
                initial={prefersReducedMotion ? false : { opacity: 0, x: 40, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                transition={{
                  duration: 0.5,
                  ease: EASE_OVERSHOOT,
                  delay: notif.delay,
                }}
              >
                {/* Match card confetti */}
                {notif.isMatch && (
                  <>
                    <ConfettiDot
                      delay={2.5}
                      color="#5B9279"
                      style={{ top: '-12px', left: '25%' }}
                    />
                    <ConfettiDot
                      delay={2.6}
                      color="#E8985A"
                      style={{ top: '-18px', left: '50%' }}
                    />
                    <ConfettiDot
                      delay={2.55}
                      color="#6B9BD2"
                      style={{ top: '-10px', right: '25%' }}
                    />
                  </>
                )}

                {/* Match card glow ring */}
                {notif.isMatch && (
                  <motion.span
                    className="pointer-events-none absolute -inset-[3px] rounded-[18px] border-2 border-primary"
                    initial={{ opacity: 0, scale: 1 }}
                    animate={{ opacity: [0, 0.5, 0], scale: [1, 1.05, 1.05] }}
                    transition={{ duration: 0.8, ease: 'easeOut', delay: 2.4 }}
                  />
                )}

                <div className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${notif.dot}`} />
                <div>
                  <h4 className="text-[13px] font-bold text-foreground">{notif.title}</h4>
                  <p className="mt-px text-[11px] text-muted-foreground">
                    {notif.subtitle}
                    {notif.isMatch && ' \u00b7 Verified \u2728'}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Problem Section
// ---------------------------------------------------------------------------

const painCards = [
  {
    icon: AlertTriangle,
    iconBg: 'bg-destructive/[0.08]',
    iconColor: 'text-destructive',
    title: 'Guaranteed Reliability',
    description:
      'Enjoy peace of mind with vetted professionals, strict deadlines, and a commitment to quality.',
    avatar: '\uD83D\uDE14',
    quote: '"He said two weeks. It\'s been two months."',
  },
  {
    icon: DollarSign,
    iconBg: 'bg-accent/[0.15]',
    iconColor: 'text-accent',
    title: 'Cost-Effective Excellence',
    description:
      'Access premium talent without the bloated agency overhead, ensuring maximum value.',
    avatar: '\uD83D\uDCB8',
    quote: '"$80k for a booking page? We\'re a bakery."',
  },
  {
    icon: HelpCircle,
    iconBg: 'bg-primary/[0.08]',
    iconColor: 'text-primary',
    title: 'Verified Track Records',
    description:
      'Work with true experts whose skills and past successes have been thoroughly validated.',
    avatar: '\uD83E\uDD14',
    quote: '"His portfolio looked great. The code was a disaster."',
  },
]

function ProblemSection() {
  const prefersReducedMotion = useReducedMotion()

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: EASE_STANDARD, delay: i * 0.12 },
    }),
  }

  return (
    <section className="bg-card px-8 py-[120px]">
      <div className="mx-auto max-w-[1120px]">
        <motion.div
          className="mb-4 text-xs font-bold uppercase tracking-[2.5px] text-accent"
          initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.65, ease: EASE_STANDARD }}
        >
          The Opportunity
        </motion.div>

        <motion.h2
          className="mb-5 max-w-[580px] text-foreground"
          style={{
            fontFamily: 'Fraunces, Georgia, serif',
            fontSize: '42px',
            fontWeight: 700,
            lineHeight: 1.15,
            letterSpacing: '-0.5px',
          }}
          initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.1 }}
        >
          Great Developers Are Here.{' '}
          <em className="not-italic text-accent">And Trust Is Built-In.</em>
        </motion.h2>

        <motion.p
          className="mb-14 max-w-[560px] text-[17px] leading-[1.65] text-muted-foreground"
          initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.2 }}
        >
          Empower your business with top-tier talent. Choose a streamlined path to success with our vetted professionals, ensuring your projects are delivered efficiently and to the highest standards. Accelerate your growth with custom tools built just for you.
        </motion.p>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {painCards.map((card, i) => (
            <motion.div
              key={card.title}
              className="group rounded-[20px] border border-border bg-background p-7 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-accent hover:shadow-[0_12px_32px_rgba(45,42,38,0.08)]"
              variants={cardVariants}
              custom={i}
              initial={prefersReducedMotion ? false : 'hidden'}
              whileInView="visible"
              viewport={{ once: true, amount: 0.15 }}
            >
              <div
                className={`mb-5 flex h-12 w-12 items-center justify-center rounded-[14px] ${card.iconBg} transition-transform duration-300 group-hover:-rotate-3 group-hover:scale-110`}
              >
                <card.icon className={`h-6 w-6 ${card.iconColor}`} />
              </div>
              <h3 className="mb-2 text-lg font-bold text-foreground">{card.title}</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">{card.description}</p>
              <div className="mt-6 flex items-center gap-3 border-t border-border pt-5">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-muted text-sm">
                  {card.avatar}
                </div>
                <span className="text-xs italic leading-snug text-muted-foreground">
                  {card.quote}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Trust Pillars Section
// ---------------------------------------------------------------------------

const pillars = [
  {
    icon: FileText,
    title: 'Structured Contracts',
    description:
      'Our guided process helps you define clear requirements, timelines, and acceptance criteria. Total clarity from day one. Everyone knows what "done" looks like.',
  },
  {
    icon: Users,
    title: 'Curated Marketplace',
    description:
      'Get matched with elite, vetted developers who have proven expertise in your industry and tech stack. Find the perfect fit, not just a random freelancer.',
  },
  {
    icon: ShieldCheck,
    title: 'Protected Payments',
    description:
      'Your funds are held securely and only released when you approve the work. This ensures developers are motivated to deliver on their promises and protects you from risk.',
  },
]

function TrustSection() {
  const prefersReducedMotion = useReducedMotion()

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: EASE_STANDARD, delay: i * 0.12 },
    }),
  }

  return (
    <section className="bg-background px-8 py-[120px]">
      <div className="mx-auto max-w-[1120px]">
        {/* Header */}
        <div className="mb-[72px] text-center">
          <motion.div
            className="mb-4 text-xs font-bold uppercase tracking-[2.5px] text-primary"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.65, ease: EASE_STANDARD }}
          >
            Why Masonyt
          </motion.div>

          <motion.h2
            className="mb-3 text-foreground"
            style={{
              fontFamily: 'Fraunces, Georgia, serif',
              fontSize: '42px',
              fontWeight: 700,
              letterSpacing: '-0.5px',
            }}
            initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.1 }}
          >
            Your Bridge to
            <br />
            Reliable Software
          </motion.h2>

          <motion.p
            className="mx-auto max-w-[460px] text-[17px] text-muted-foreground"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.2 }}
          >
            We replaced the uncertainty with a clear, guaranteed process.
          </motion.p>
        </div>

        {/* Pillar cards */}
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {pillars.map((pillar, i) => (
            <motion.div
              key={pillar.title}
              className="group rounded-[20px] border border-border bg-card p-7 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-primary hover:shadow-[0_12px_32px_rgba(45,42,38,0.06)]"
              variants={cardVariants}
              custom={i}
              initial={prefersReducedMotion ? false : 'hidden'}
              whileInView="visible"
              viewport={{ once: true, amount: 0.15 }}
            >
              <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/[0.08] transition-transform duration-300 group-hover:-rotate-3 group-hover:scale-110">
                <pillar.icon className="h-[26px] w-[26px] text-primary" />
              </div>
              <h3 className="mb-2.5 text-xl font-bold text-foreground">{pillar.title}</h3>
              <p className="text-sm leading-[1.65] text-muted-foreground">{pillar.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Trust stats */}
        <motion.div
          className="mt-14 flex flex-wrap justify-center gap-16 border-t border-border pt-12 max-md:gap-8"
          initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.65, ease: EASE_STANDARD }}
        >
          <AnimatedStat end={100} suffix="%" label="Payment Protection" />
          <AnimatedStat end={48} suffix="h" label="Average Match Time" />
          <AnimatedStat end={4.9} suffix="/5" label="Developer Rating" isFloat />
        </motion.div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// How It Works Section
// ---------------------------------------------------------------------------

const steps = [
  {
    num: 1,
    title: 'Post Your Project',
    description:
      'Describe what you need in plain language. Our guided process helps you define clear requirements, no tech knowledge needed.',
    illustration: ILLUSTRATIONS.working,
    illustrationAlt: 'Person posting a project',
  },
  {
    num: 2,
    title: 'Get Matched',
    description:
      'Vetted developers with proven expertise submit proposals. Pick the right fit with full confidence.',
    illustration: ILLUSTRATIONS.hiring,
    illustrationAlt: 'Hiring and matching',
  },
  {
    num: 3,
    title: 'Build with Confidence',
    description:
      'Work progresses through milestones. Funds held in escrow, released only when you approve each delivery.',
    illustration: ILLUSTRATIONS.businessDeal,
    illustrationAlt: 'Protected business deal',
  },
]

function HowItWorksSection() {
  const prefersReducedMotion = useReducedMotion()

  return (
    <section id="how-it-works" className="relative bg-card px-8 py-[120px]">
      {/* Subtle gradient */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 50% 40% at 70% 20%, rgba(91,146,121,0.04) 0%, transparent 70%)',
        }}
      />

      <div className="relative mx-auto max-w-[1120px]">
        {/* Header */}
        <div className="mb-20 text-center">
          <motion.div
            className="mb-4 text-xs font-bold uppercase tracking-[2.5px] text-primary"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.65, ease: EASE_STANDARD }}
          >
            How It Works
          </motion.div>

          <motion.h2
            className="mb-3 text-foreground"
            style={{
              fontFamily: 'Fraunces, Georgia, serif',
              fontSize: '42px',
              fontWeight: 700,
              letterSpacing: '-0.5px',
            }}
            initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.1 }}
          >
            Three Steps to Your
            <br />
            Perfect Build
          </motion.h2>

          <motion.p
            className="mx-auto max-w-[500px] text-[17px] text-muted-foreground"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.2 }}
          >
            A clear, structured process that protects both sides.
          </motion.p>
        </div>

        {/* Steps */}
        <div className="flex flex-col gap-20">
          {steps.map((step, i) => {
            const isEven = i % 2 === 1
            const illustrationSlideX = isEven ? -40 : 40

            return (
              <div
                key={step.num}
                className={`grid grid-cols-1 items-center gap-16 lg:grid-cols-2 ${isEven ? 'lg:direction-rtl' : ''}`}
                style={isEven ? { direction: 'rtl' } : undefined}
              >
                {/* Text */}
                <div style={isEven ? { direction: 'ltr' } : undefined}>
                  <motion.div
                    className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-full border-[3px] border-primary bg-card text-primary shadow-[0_4px_12px_rgba(45,42,38,0.06)] transition-all duration-300 hover:scale-[1.08] hover:bg-primary hover:text-primary-foreground"
                    style={{
                      fontFamily: 'Fraunces, Georgia, serif',
                      fontSize: '18px',
                      fontWeight: 700,
                    }}
                    initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                  >
                    {step.num}
                  </motion.div>

                  <motion.h3
                    className="mb-2.5 text-[26px] font-bold text-foreground"
                    initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ duration: 0.65, ease: EASE_STANDARD }}
                  >
                    {step.title}
                  </motion.h3>

                  <motion.p
                    className="max-w-[420px] text-base leading-[1.65] text-muted-foreground"
                    initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.1 }}
                  >
                    {step.description}
                  </motion.p>
                </div>

                {/* Illustration */}
                <motion.div
                  className="flex items-center justify-center rounded-3xl border border-border bg-background p-6 transition-shadow duration-300 hover:shadow-[0_8px_32px_rgba(45,42,38,0.06)]"
                  style={isEven ? { direction: 'ltr' } : undefined}
                  initial={prefersReducedMotion ? false : { opacity: 0, x: illustrationSlideX }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.7, ease: EASE_STANDARD }}
                >
                  <img
                    src={step.illustration}
                    alt={step.illustrationAlt}
                    className="h-auto w-full max-w-[380px]"
                    loading="lazy"
                  />
                </motion.div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Final CTA Section
// ---------------------------------------------------------------------------

function FinalCTASection() {
  const prefersReducedMotion = useReducedMotion()

  return (
    <section className="relative bg-background px-8 py-[140px] text-center">
      {/* Subtle gradient */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(232,152,90,0.05) 0%, transparent 70%)',
        }}
      />

      <div className="relative z-10">
        <motion.h2
          className="mb-4 text-foreground"
          style={{
            fontFamily: 'Fraunces, Georgia, serif',
            fontSize: 'clamp(36px, 4vw, 48px)',
            fontWeight: 700,
            letterSpacing: '-1px',
          }}
          initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.65, ease: EASE_STANDARD }}
        >
          Ready to Build
          <br />
          with Confidence?
        </motion.h2>

        <motion.p
          className="mx-auto mb-10 max-w-[460px] text-lg leading-relaxed text-muted-foreground"
          initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.1 }}
        >
          Post your project for free and get matched with developers who deliver.
        </motion.p>

        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.65, ease: EASE_STANDARD, delay: 0.2 }}
        >
          <LandingButton to="/post" size="lg">
            Get Your Project Started
            <ArrowRight className="h-5 w-5 transition-transform" />
          </LandingButton>
        </motion.div>

        {/* Agreement illustration */}
        <motion.div
          className="mt-12"
          initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, ease: EASE_STANDARD, delay: 0.3 }}
        >
          <img
            src={ILLUSTRATIONS.agreement}
            alt="Agreement reached"
            className="mx-auto h-auto max-w-[400px]"
            loading="lazy"
          />
        </motion.div>
      </div>
    </section>
  )
}

export default function Home() {
  return (
    <PublicLayout>
      <HeroSection />
      <ProblemSection />
      <TrustSection />
      <HowItWorksSection />
      <FinalCTASection />
    </PublicLayout>
  )
}
